import { FocusEvent, FormEvent, useEffect } from 'react';
import { FieldValues, Path, PathValue, useController, useFormContext } from 'react-hook-form';

import { ReactComponent as MinusIcon } from 'assets/icons/MinusIcon.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/PlusIcon.svg';

import { TextInput } from 'components/_basic/Form/TextInput';

import { CHANGE_VALUES, onlyNumbersRegex } from './constants';
import { changeCounterOnAdornmentClick, getNumWithPostfixString, getRegexMatchString } from './helpers';
import { ICounterInputControlProps } from './types';

import styles from './CounterInputControl.module.scss';

export const CounterInputControl = <T extends FieldValues>(props: ICounterInputControlProps<T>) => {
  const {
    name,
    control,
    rules,
    defaultValue,
    onBlur,
    regex,
    postfix = '',
    maxValue,
    minValue,
    shift = 1,
    ...restProps
  } = props;

  const { field, fieldState } = useController({
    name,
    control,
    rules,
    defaultValue,
  });

  const { setValue } = useFormContext();

  const { ref, value, onBlur: onFieldBlur, ...restField } = field;

  const { error } = fieldState;

  const handleBlur = (e?: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFieldBlur();
    if (onBlur && e) onBlur(e);

    if (!e) return;

    const { value } = e.target;
    const formattedValue = getRegexMatchString(value, regex, postfix) as PathValue<T, Path<T>>;

    setValue(name, formattedValue, { shouldValidate: true });
  };

  const handleBeforeInput = (e?: FormEvent<HTMLDivElement>) => {
    if (!e) return;

    const target = e.target as HTMLInputElement;
    const { data } = e.nativeEvent as InputEvent;
    const { value } = target;

    const start = target.selectionStart ?? 0;
    const end = target.selectionEnd ?? 0;

    const newValue = value.slice(0, start) + data + value.slice(end);

    if (!regex.test(newValue) && !onlyNumbersRegex.test(newValue)) e.preventDefault();
  };

  const increment = () => {
    const newVal = changeCounterOnAdornmentClick(value, CHANGE_VALUES.ADD, shift, maxValue, minValue);

    const formattedValue = getNumWithPostfixString(newVal, postfix) as PathValue<T, Path<T>>;

    setValue(name, formattedValue, { shouldValidate: true });
  };

  const decrement = () => {
    const newVal = changeCounterOnAdornmentClick(value, CHANGE_VALUES.SUBTRACT, shift, maxValue, minValue) as PathValue<
      T,
      Path<T>
    >;

    const formattedValue = getNumWithPostfixString(newVal, postfix) as PathValue<T, Path<T>>;

    setValue(name, formattedValue, { shouldValidate: true });
  };

  // Update formatted value when postfix (i18n) changes
  useEffect(() => {
    if (!value) return;

    const numericPart = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
    if (Number.isNaN(numericPart)) return;

    const formattedValue = getNumWithPostfixString(numericPart, postfix) as PathValue<T, Path<T>>;
    setValue(name, formattedValue, { shouldValidate: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postfix]);

  return (
    <TextInput
      inputRef={ref}
      value={value ?? ''}
      fieldError={error}
      onBlur={handleBlur}
      onBeforeInput={handleBeforeInput}
      onEndAdornmentClick={increment}
      onStartAdornmentClick={decrement}
      {...restField}
      {...restProps}
      inputProps={{
        ...restProps.inputProps,
        className: styles.textCenter,
      }}
      className={styles.startAdornmentBorder}
      endAdornmentIcon={<PlusIcon />}
      startAdornmentIcon={<MinusIcon />}
      readOnly
    />
  );
};
